import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { PlacementView } from "../../api/layouts";
import { markerCenter, svgPoints } from "../../lib/layoutGeometry";
import Ic from "../icons.jsx";
import type { CSSProperties } from "react";

/**
 * "Where is it on the layout" card (contract §5): the parent layout's image in
 * its aspect ratio, the marker drawn in its state colour and everything but a
 * ~120 px spotlight around it slightly darkened (radial-gradient mask, no
 * animation). Clicking opens the layout with that marker selected.
 */
export function PlacementPreview({ placements, positionId }: {
  /** Sorted nearest first (`distance`, then layout name) — the first one is shown big. */
  placements: PlacementView[];
  /** The position whose card this is — the marker link selects it (or its marked ancestor). */
  positionId: string;
}) {
  const { t } = useTranslation();
  const first = placements[0];
  if (!first) return null;
  const rest = placements.slice(1);
  return (
    <div className="layout-preview">
      <PreviewLink placement={first} positionId={positionId} big />
      {rest.length > 0 && (
        <ul className="layout-preview-list">
          {rest.map((p) => (
            <li key={`${p.layoutId}-${p.marker.id}`}>
              <Link to={`/layout/${p.layoutId}?marker=${p.placedPositionId}`} className={`layout-preview-row${p.kind === "MODEL" ? " is-model" : ""}`}>
                {p.kind === "MODEL"
                  ? <span className="layout-thumb-model" aria-hidden="true"><Ic.box /></span>
                  : <img src={p.thumbnailUrl ?? undefined} alt="" loading="lazy" />}
                <span className="layout-preview-row-text">
                  <b>{p.layoutName}</b>
                  <span className="muted">{captionFor(t, p)}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

type Translate = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

function captionFor(t: Translate, p: PlacementView): string {
  const model = p.kind === "MODEL";
  if (p.distance === 0) {
    return model
      ? t("layout.preview.onModel", "Na 3D layoutu: {{layout}}", { layout: p.layoutName })
      : t("layout.preview.onLayout", "Na layoutu: {{layout}}", { layout: p.layoutName });
  }
  return model
    ? t("layout.preview.onModelAs", "Na 3D layoutu: {{layout}} (kao: {{as}})", { layout: p.layoutName, as: p.placedPositionName })
    : t("layout.preview.onLayoutAs", "Na layoutu: {{layout}} (kao: {{as}})", { layout: p.layoutName, as: p.placedPositionName });
}

function PreviewLink({ placement: p, positionId, big }: { placement: PlacementView; positionId: string; big?: boolean }) {
  const { t } = useTranslation();
  const target = p.distance === 0 ? positionId : p.placedPositionId;
  // A 3D layout has no picture to spotlight: a row that opens the model with the marker framed (§9).
  if (p.kind === "MODEL") {
    return (
      <Link to={`/layout/${p.layoutId}?marker=${target}`} className="layout-preview-model" aria-label={captionFor(t, p)}>
        <Ic.box aria-hidden="true" />
        <span className="layout-preview-model-text">
          <b>{captionFor(t, p)}</b>
          <span className="muted">{p.layoutPositionName}{p.layoutPositionCode ? ` · ${p.layoutPositionCode}` : ""}</span>
        </span>
        <span className={`layout-dot is-${p.marker.state.toLowerCase()}`} aria-hidden="true" />
      </Link>
    );
  }
  const c = markerCenter(p.marker);
  const state = p.marker.state.toLowerCase();
  const ratio = p.imageWidth && p.imageHeight ? p.imageWidth / p.imageHeight : 4 / 3;
  const frameStyle = { "--layout-ratio": ratio } as CSSProperties;
  const maskStyle = { "--layout-spot-x": `${c.x * 100}%`, "--layout-spot-y": `${c.y * 100}%` } as CSSProperties;
  return (
    <Link to={`/layout/${p.layoutId}?marker=${target}`} className={`layout-preview-link${big ? " is-big" : ""}`} aria-label={captionFor(t, p)}>
      <div className="layout-preview-frame" style={frameStyle}>
        <img src={p.imageUrl ?? undefined} alt="" loading="lazy" />
        <div className="layout-preview-shade" style={maskStyle} aria-hidden="true" />
        {p.marker.shape === "POLYGON" && p.marker.points ? (
          <svg className={`layout-preview-poly is-${state}`} viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
            <polygon points={svgPoints(p.marker.points)} vectorEffect="non-scaling-stroke" />
          </svg>
        ) : p.marker.shape === "ZONE" || p.marker.shape === "CIRCLE" ? (
          <span
            className={`layout-preview-zone is-${state}${p.marker.shape === "CIRCLE" ? " is-circle" : ""}`}
            style={{ left: `${p.marker.x * 100}%`, top: `${p.marker.y * 100}%`, width: `${(p.marker.w ?? 0) * 100}%`, height: `${(p.marker.h ?? 0) * 100}%` }}
            aria-hidden="true"
          />
        ) : (
          <span className={`layout-preview-pin is-${state}`} style={{ left: `${c.x * 100}%`, top: `${c.y * 100}%` }} aria-hidden="true" />
        )}
      </div>
      <div className="layout-preview-caption">
        <span>{captionFor(t, p)}</span>
        <span className="muted">{p.layoutPositionName}{p.layoutPositionCode ? ` · ${p.layoutPositionCode}` : ""}</span>
      </div>
    </Link>
  );
}
